import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody } from "reactstrap";

import ConfirmModal from "../../../Components/Common/ConfirmModal";
import { AppDispatch } from "../../../store";
import { showAlertWithTimeout } from "../../../slices/Alert/reducer";
import {
    createMenuOption,
    deleteMenuOption,
    getMenuOptions,
    updateMenuOption,
} from "../../../slices/MenuOptions/thunk";
import { initialState as menuOptionsInitialState } from "../../../slices/MenuOptions/reducer";
import HeaderSidebarToggle from "../../../Components/Common/HeaderSidebarToggle";
import { getOptionMenuCount } from "../../../helpers/helpers";
import HomeLogoLink from "../../../Components/Common/HomeLogoLink";

const getOptionIdValue = (option: any): string | number | undefined => {
    if (!option || typeof option !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "optionId", "option_id", "uuid", "_id", "slug", "code", "key"];

    for (const key of candidateKeys) {
        const value = (option as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const getOptionName = (option: any): string => {
    if (!option || typeof option !== "object") {
        return "Tùy chọn";
    }

    const candidateKeys = ["name", "optionName", "title", "label", "displayName"];

    for (const key of candidateKeys) {
        const value = (option as Record<string, any>)[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }

    return "Tùy chọn";
};

const getOptionSelectionType = (option: any): "single" | "multiple" => {
    if (!option || typeof option !== "object") {
        return "single";
    }

    const value =
        (option as Record<string, any>).selection_type ??
        (option as Record<string, any>).selectionType ??
        (option as Record<string, any>).select_type ??
        (option as Record<string, any>).selectType;

    if (typeof value === "string") {
        return value.trim().toLowerCase() === "multiple" ? "multiple" : "single";
    }

    if (typeof value === "number") {
        return value === 2 ? "multiple" : "single";
    }

    return "single";
};

const parseBooleanLike = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (value === 1) {
            return true;
        }
        if (value === 0) {
            return false;
        }
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized.length === 0) {
            return undefined;
        }

        const truthyValues = [
            "1",
            "true",
            "yes",
            "y",
            "active",
            "available",
            "enabled",
            "on",
            "open",
        ];
        const falsyValues = [
            "0",
            "false",
            "no",
            "n",
            "inactive",
            "unavailable",
            "disabled",
            "off",
            "closed",
        ];

        if (truthyValues.includes(normalized)) {
            return true;
        }

        if (falsyValues.includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

const getOptionIsActive = (option: any): boolean => {
    if (!option || typeof option !== "object") {
        return true;
    }

    const candidateKeys = ["active", "isActive", "enabled", "available", "status", "state"];

    for (const key of candidateKeys) {
        const value = (option as Record<string, any>)[key];
        const parsed = parseBooleanLike(value);
        if (parsed !== undefined) {
            return parsed;
        }

        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            if (["active", "available", "enabled"].includes(normalized)) {
                return true;
            }
            if (["inactive", "disabled", "unavailable"].includes(normalized)) {
                return false;
            }
        }
    }

    return true;
};

const optionInfoColumnStyle: React.CSSProperties = { width: "auto", wordBreak: "break-word" };
const actionsColumnStyle: React.CSSProperties = { width: "92px", whiteSpace: "nowrap" };

const MenuOptionsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { options, loading, submitting, error } = useSelector(
        (state: any) => state.MenuOptions ?? menuOptionsInitialState
    );
    const optionList = useMemo(() => (Array.isArray(options) ? options : []), [options]);

    const [showFormModal, setShowFormModal] = useState(false);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [formName, setFormName] = useState("");
    const [formNameError, setFormNameError] = useState("");
    const [formSelectionType, setFormSelectionType] = useState<"single" | "multiple">("single");
    const [formStatus, setFormStatus] = useState(true);
    const [formError, setFormError] = useState("");
    const [editingOptionId, setEditingOptionId] = useState<string | number | null>(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<string | number | null>(null);
    const [deleteTargetName, setDeleteTargetName] = useState("");
    const [deleteTargetMenuCount, setDeleteTargetMenuCount] = useState(0);

    useEffect(() => {
        dispatch(getMenuOptions());
    }, [dispatch]);

    const resetForm = () => {
        setFormName("");
        setFormNameError("");
        setFormSelectionType("single");
        setFormStatus(true);
        setFormError("");
        setEditingOptionId(null);
        setFormMode("create");
    };

    const closeFormModal = () => {
        setShowFormModal(false);
        resetForm();
    };

    const openCreateModal = () => {
        resetForm();
        setShowFormModal(true);
    };

    const openEditModal = (option: any) => {
        const optionId = getOptionIdValue(option);
        setEditingOptionId(optionId ?? null);
        setFormMode("edit");
        setFormName(getOptionName(option));
        setFormSelectionType(getOptionSelectionType(option));
        setFormStatus(getOptionIsActive(option));
        setFormNameError("");
        setFormError("");
        setShowFormModal(true);
    };

    const buildOptionPayload = (name: string, selectionType: "single" | "multiple", isActive: boolean) => ({
        name,
        optionName: name,
        title: name,
        label: name,
        displayName: name,
        selection_type: selectionType,
        selectionType,
        active: isActive,
        isActive: isActive,
        enabled: isActive,
        available: isActive,
        status: isActive ? "active" : "inactive",
        state: isActive ? "active" : "inactive",
    });

    const handleSubmitForm = async () => {
        const trimmedName = formName.trim();
        setFormNameError("");
        setFormError("");

        if (trimmedName.length === 0) {
            setFormNameError("Vui lòng nhập tên tùy chọn");
            return;
        }

        const payload = buildOptionPayload(trimmedName, formSelectionType, formStatus);

        try {
            if (formMode === "edit") {
                if (editingOptionId === null || editingOptionId === undefined) {
                    setFormError("Không xác định được tùy chọn cần cập nhật.");
                    return;
                }

                await dispatch(
                    updateMenuOption({
                        optionId: editingOptionId,
                        data: payload,
                    })
                ).unwrap();
                dispatch(
                    showAlertWithTimeout(
                        "success",
                        "Thành công",
                        "Cập nhật tùy chọn thành công!"
                    )
                );
            } else {
                await dispatch(createMenuOption(payload)).unwrap();
                dispatch(
                    showAlertWithTimeout(
                        "success",
                        "Thành công",
                        "Thêm tùy chọn thành công!"
                    )
                );
            }

            closeFormModal();
            dispatch(getMenuOptions());
        } catch (submitError) {
            console.error(submitError);
            setFormError("Không thể lưu tùy chọn. Vui lòng thử lại.");
        }
    };

    const openDeleteConfirmation = (option: any) => {
        const optionId = getOptionIdValue(option);
        if (optionId === undefined || optionId === null) {
            dispatch(
                showAlertWithTimeout(
                    "warning",
                    "Cảnh báo",
                    "Không xác định được tùy chọn để xóa."
                )
            );
            return;
        }

        setDeleteTargetId(optionId);
        setDeleteTargetName(getOptionName(option));
        setDeleteTargetMenuCount(getOptionMenuCount(option));
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteTargetId(null);
        setDeleteTargetName("");
        setDeleteTargetMenuCount(0);
    };

    const handleConfirmDelete = async () => {
        if (deleteTargetId === null || deleteTargetId === undefined) {
            return;
        }

        try {
            await dispatch(deleteMenuOption(deleteTargetId)).unwrap();
            dispatch(
                showAlertWithTimeout(
                    "success",
                    "Thành công",
                    "Xóa tùy chọn thành công!"
                )
            );
            closeDeleteModal();
            dispatch(getMenuOptions());
        } catch (deleteError) {
            console.error(deleteError);
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Thất bại",
                    "Xóa tùy chọn thất bại. Vui lòng thử lại."
                )
            );
        }
    };

    const renderTableBody = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={2} className="text-center py-3">
                        Đang tải danh sách tùy chọn...
                    </td>
                </tr>
            );
        }

        if (optionList.length === 0) {
            return (
                <tr>
                    <td colSpan={2} className="text-center py-3">
                        {error ?? "Chưa có tùy chọn nào."}
                    </td>
                </tr>
            );
        }

        return optionList.map((option: any, index: number) => {
            const optionId = getOptionIdValue(option);
            const selectionType = getOptionSelectionType(option);
            const isActive = getOptionIsActive(option);
            const selectionLabel = selectionType === "multiple" ? "Chọn nhiều" : "Chọn 1";
            const statusClass = isActive ? "badge bg-success" : "badge bg-secondary";
            const statusLabel = isActive ? "Đang bán" : "Ngưng bán";

            return (
                <tr key={optionId ?? `option-${index}`}>
                    <td style={optionInfoColumnStyle}>
                        {optionId ? (
                            <Link className="fw-medium d-block" to={`/menu/options/${optionId}`}>
                                {getOptionName(option)}
                            </Link>
                        ) : (
                            <span className="fw-medium d-block">{getOptionName(option)}</span>
                        )}
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-1 small text-muted">
                            <span>{getOptionMenuCount(option)} món</span>
                            <span>{selectionLabel}</span>
                            <span className={statusClass}>{statusLabel}</span>
                        </div>
                    </td>
                    <td className="text-end align-middle" style={actionsColumnStyle}>
                        <div className="d-inline-flex align-items-center gap-1">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm text-white"
                                onClick={() => openEditModal(option)}
                            >
                                <i className="mdi mdi-lead-pencil"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm text-white"
                                onClick={() => openDeleteConfirmation(option)}
                            >
                                <i className="mdi mdi-delete"></i>
                            </button>
                        </div>
                    </td>
                </tr>
            );
        });
    };

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={showDeleteModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content={
                    deleteTargetName
                        ? `Bạn có chắc chắn muốn xóa tùy chọn "${deleteTargetName}"?${
                              deleteTargetMenuCount > 0
                                  ? ` Tùy chọn này đang được dùng bởi ${deleteTargetMenuCount} món; các món này sẽ mất tùy chọn sau khi xóa.`
                                  : ""
                          }`
                        : "Bạn có chắc chắn muốn xóa tùy chọn này không?"
                }
                onConfirm={() => {
                    setShowDeleteModal(false);
                    handleConfirmDelete();
                }}
                onCancel={closeDeleteModal}
            />

            <Modal size="sm" isOpen={showFormModal} toggle={closeFormModal} centered>
                <ModalBody>
                    <h6 className="fs-15 mb-3">
                        {formMode === "edit" ? "Chỉnh sửa tùy chọn" : "Thêm tùy chọn mới"}
                    </h6>
                    <div className="mb-3">
                        <label className="form-label">Tên tùy chọn</label>
                        <input
                            className={`form-control ${formNameError ? "is-invalid" : ""}`}
                            type="text"
                            placeholder="VD: Size, Đá, Ngọt, Topping, ..."
                            value={formName}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setFormName(event.target.value);
                                if (formNameError) {
                                    setFormNameError("");
                                }
                            }}
                        />
                        {formNameError && <div className="invalid-feedback d-block">{formNameError}</div>}
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Kiểu chọn</label>
                        <select
                            className="form-select"
                            value={formSelectionType}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                setFormSelectionType(event.target.value === "multiple" ? "multiple" : "single");
                            }}
                        >
                            <option value="single">Chọn 1</option>
                            <option value="multiple">Chọn nhiều</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Trạng thái</label>
                        <select
                            className="form-select"
                            value={formStatus ? "true" : "false"}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                setFormStatus(event.target.value === "true");
                            }}
                        >
                            <option value="true">Đang bán</option>
                            <option value="false">Ngưng bán</option>
                        </select>
                    </div>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeFormModal} disabled={submitting}>
                        Hủy
                    </Button>
                    <Button color="primary" onClick={handleSubmitForm} disabled={submitting}>
                        {submitting ? "Đang lưu..." : "Lưu"}
                    </Button>
                </div>
            </Modal>

            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Tùy chọn món</h6>
                    </div>

                    <div className="d-flex align-items-center">
                        <div className="filter-option" onClick={openCreateModal} role="button">
                            <i className="mdi mdi-plus"></i>
                        </div>
                        <HeaderSidebarToggle className="ms-2" />
                    </div>
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">
                        <div className="card">
                            <div className="card-body p-0">
                                <div className="w-100">
                                    <table className="table mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
                                        <thead>
                                            <tr>
                                                <th style={optionInfoColumnStyle}>Tùy chọn</th>
                                                <th className="text-end" style={actionsColumnStyle}>Thao tác</th>
                                            </tr>
                                        </thead>
                                        <tbody>{renderTableBody()}</tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default MenuOptionsPage;
